import { useState } from "react"
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@radix-ui/react-popover"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"

import { ConsumerConnectionBillingStatusType } from "@/types/billing"
import { canViewThisPage, validateMapPermission } from "@/lib/api/auth"
import { getConnectionAndBillingStatus } from "@/lib/api/billing"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Icons } from "@/components/icons"
import { Loader } from "@/components/loader"
import { MapFilter } from "@/components/map/map-filter"
import { MapLegends } from "@/components/map/map-legends"
import { MapRenderer } from "@/components/map/map-libre/map-renderer"

export const Route = createFileRoute("/_home/map")({
  beforeLoad: async () => {
    validateMapPermission()

    if (canViewThisPage("himo_billing.view_map")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: MapPage,
})

function MapPage() {
  const [zones, setZones] = useState<number[]>([])
  const [books, setBooks] = useState<number[]>([])
  const [statusFilters, setStatusFilters] = useState<string[]>([])
  const { data: markers, isLoading } = useQuery<
    ConsumerConnectionBillingStatusType[]
  >({
    queryKey: ["markers"],
    queryFn: () => getConnectionAndBillingStatus(),
  })

  console.log(markers)

  // Filter markers based on selected statuses, zones, and books
  const filteredMarkers = markers?.filter((marker) => {
    // Filter by status if any status filters are selected
    const statusMatch =
      statusFilters.length === 0 ||
      statusFilters.includes(marker.connection_status) ||
      statusFilters.includes(marker.billing_status)

    // Filter by zone/book if any are selected
    const zoneSelected = zones.length > 0
    const bookSelected = books.length > 0

    const locationMatch =
      (!zoneSelected && !bookSelected) || // no filters
      (zoneSelected && !bookSelected && zones.includes(marker.zone_id)) || // only zone
      (!zoneSelected && bookSelected && books.includes(marker.zone_id)) ||
      (zoneSelected &&
        bookSelected &&
        zones.includes(marker.zone_id) &&
        books.includes(marker.book_id)) // both

    return statusMatch && locationMatch
  })

  console.log(filteredMarkers)
  return (
    <main>
      {isLoading ? (
        <div className="flex h-screen items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          <MapRenderer markers={filteredMarkers} />
          <div className="left-30 fixed bottom-0 z-[500] p-2 md:p-4">
            <MapLegends
              onStatusFilterChange={setStatusFilters}
              selectedStatuses={statusFilters}
            />
          </div>
          {/* Filter */}
          <div className="fixed right-0 top-10 z-[500] p-2 md:p-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-half h-10 w-10 shadow-sm transition-shadow hover:shadow-md"
                >
                  <Icons.mixer className="h-10 w-10" />{" "}
                  {/* Increased icon size */}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-55 border-0 p-0"
                align="end"
                sideOffset={8}
              >
                <Card className="w-full overflow-hidden rounded-xl rounded-tr-none shadow-lg">
                  <CardHeader className="rounded-tl-xl rounded-tr-none bg-primary p-4">
                    {" "}
                    {/* Increased padding */}
                    <CardTitle className="flex items-center justify-between text-lg font-semibold text-white">
                      {" "}
                      {/* Larger text */}
                      Filter {/* Uppercase for emphasis */}
                      <Button
                        variant="destructive"
                        className="flex cursor-pointer items-center gap-1"
                        onClick={() => {
                          setZones([])
                          setBooks([])
                        }}
                      >
                        <Icons.x height="15px" width="15px" />
                        Clear Filter
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 pt-4">
                    {" "}
                    {/* Increased padding */}
                    <MapFilter
                      setZones={setZones}
                      zones={zones}
                      books={books}
                      setBooks={setBooks}
                    />
                  </CardContent>
                </Card>
              </PopoverContent>
            </Popover>
          </div>
        </>
      )}
    </main>
  )
}
